import { designVariables } from "@studio3t/design-variables";
import {
  BadgeConfirmationSubtle,
  BadgeErrorSubtle,
  BadgeWarningSubtle,
} from "@studio3t/generic-components";
import type { ReactNode } from "react";
import styled from "styled-components";
import { OverlayScroll } from "@/components/OverlayScroll";
import { SectionCard } from "@/components/SectionCard";
import { SkeletonBar } from "@/components/Skeleton";
import { useJobMetricsPolling } from "@/hooks/useApi";
import type {
  JobMetrics,
  PipelineProcessStats,
  WindowStatusJson,
} from "@/types/api";
import {
  formatBytes,
  formatLag,
  formatNumber,
  formatRate,
} from "@/utils/format";
import {
  type BadgeTone,
  type WindowMetricCell,
  windowMetricsDisplay,
} from "./windowMetrics";

const PRIMITIVES = designVariables.primitives;
const STATUS = designVariables.theme.light.status;
const SEMANTIC = designVariables.ui.semantic;
const HEADING = designVariables.type.heading;
const SINGLE_LINE = designVariables.type.singleLine;

// Metric cell accents (Figma node 13495-42462): a light blue tint behind the
// label and the info/subtle blue behind the value pill.
const LABEL_CELL_BG = PRIMITIVES.blue["10"]; // Figma blue/10 (#f0f6fe)
const VALUE_CELL_BG = PRIMITIVES.blue.alpha10; // Figma blue/alpha10 (#1271f11a)

// Stream-buffer segment + legend colors (Figma): queued red, in-flight green,
// done grey.
const BUFFER_COLORS = {
  queued: STATUS.error, // #f44a34
  inFlight: STATUS.confirm, // #009431
  done: PRIMITIVES.neutrals["100"], // #979797
};

interface MetricsSectionProps {
  jobName: string;
  isRunning: boolean;
}

// ── Metric grid ───────────────────────────────────────────

// Five metrics flow into a 2-column grid; the sixth slot stays empty (per
// Figma). The xs gaps let the card background show between cells. The 34px
// bottom padding (no token — derived leftover space) matches the Figma
// whitespace between the last row and the stream-buffer footer (node
// 13017-5504).
//
// auto-fit + minmax makes the grid reflow to a single column based on the
// card's own width (not the viewport): a cell needs ~321px for its label +
// value pill, so once two columns can't hold 340px each the rows wrap instead
// of clipping.
// One metric cell's width, and the floor below which the card scrolls sideways
// instead of collapsing its contents. Shared by the grid and the stream-buffer
// footer so both start scrolling at the same card width.
const CELL_MIN_WIDTH = 340; // px

// Horizontal scroll (custom scrollbars) lives on a wrapper, not the grid
// itself: putting overflow on the grid makes it a scroll container, which
// collapses the minmax(340px) floor to 0 and lets the cells shrink/clip. With
// the wrapper scrolling, the grid keeps its 340px-per-cell floor and overflows
// sideways on narrow cards.
const MetricScroll = OverlayScroll;

const MetricGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(${CELL_MIN_WIDTH}px, 1fr));
  gap: ${PRIMITIVES.spacings.xs}px;
  padding: 0 ${SEMANTIC.spacingm}px 34px;
  /* Floor the grid at one full cell so a narrow card can't squeeze the minmax
     track below its content (which would clip the value pill); the MetricScroll
     wrapper then scrolls sideways instead. Wider cards still grow to two
     columns. */
  min-width: ${CELL_MIN_WIDTH}px;
`;

const Cell = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 50px;
  padding-left: ${SEMANTIC.spacingl}px;
  overflow: hidden;
  background: ${LABEL_CELL_BG};
`;

const CellLabel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${PRIMITIVES.spacings.xs}px;
  width: 123px;
  flex-shrink: 0;
`;

const LabelText = styled.span`
  font-size: ${SINGLE_LINE.small.medium.fontSize}px;
  font-weight: ${SINGLE_LINE.small.medium.fontWeight};
  line-height: 1.2;
  text-transform: uppercase;
  color: ${(props) => props.theme.semanticTextLight};
`;

const SubText = styled.span`
  font-size: ${SINGLE_LINE.small.regular.fontSize}px;
  line-height: 1.2;
  color: ${(props) => props.theme.semanticTextDisabled};
`;

const ValuePill = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${SEMANTIC.spacings}px;
  height: 50px;
  width: 180px;
  flex-shrink: 0;
  padding: 0 ${SEMANTIC.spacingl}px;
  background: ${VALUE_CELL_BG};
`;

const ValueText = styled.span`
  font-size: ${HEADING.h5.fontSize}px;
  font-weight: ${HEADING.h5.fontWeight};
  line-height: 1.2;
  white-space: nowrap;
  color: ${(props) => props.theme.semanticTextBody};
`;

function MetricCell({
  label,
  sub,
  value,
  badge,
}: {
  label: string;
  sub?: string;
  value: ReactNode;
  badge?: ReactNode;
}) {
  return (
    <Cell>
      <CellLabel>
        <LabelText>{label}</LabelText>
        {sub != null && <SubText>{sub}</SubText>}
      </CellLabel>
      <ValuePill>
        <ValueText>{value}</ValueText>
        {badge}
      </ValuePill>
    </Cell>
  );
}

// Lag → design-system badge, mirroring the Figma "on track" pill.
function lagBadge(seconds: number): ReactNode {
  if (seconds < 0) return null;
  if (seconds < 5) {
    return <BadgeConfirmationSubtle>on track</BadgeConfirmationSubtle>;
  }
  if (seconds <= 60) {
    return <BadgeWarningSubtle>falling behind</BadgeWarningSubtle>;
  }
  return <BadgeErrorSubtle>critical backlog</BadgeErrorSubtle>;
}

// ── Stream buffer footer ──────────────────────────────────

// Scroll container (custom scrollbars): on a narrow card the buffer row, bar and
// legend scroll sideways together instead of wrapping/collapsing. FooterInner
// holds the same 340px floor as the metric grid so both regions scroll at the
// same breakpoint. OverlayScrollbars manages the overflow.
const Footer = styled(OverlayScroll)`
  border-top: 1px solid ${(props) => props.theme.cards1Border};
  padding: ${SEMANTIC.spacings}px;
`;

const FooterInner = styled.div`
  min-width: ${CELL_MIN_WIDTH}px;
`;

const BufferTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 35px;
  white-space: nowrap;
  font-size: ${SINGLE_LINE.small.medium.fontSize}px;
  font-weight: ${SINGLE_LINE.small.medium.fontWeight};
  text-transform: uppercase;
  color: ${(props) => props.theme.semanticTextLight};
`;

const BlockedText = styled.span`
  color: ${STATUS.error};
`;

const BarTrack = styled.div`
  display: flex;
  height: 20px;
  border-radius: ${PRIMITIVES.corner.s}px;
  overflow: hidden;
  background: ${(props) => props.theme.tableTheadCellBackground};
  border: 1px solid ${(props) => props.theme.cards1Border};
`;

const BarSegment = styled.div<{ $color: string; $pct: number }>`
  height: 100%;
  width: ${(props) => props.$pct}%;
  background: ${(props) => props.$color};
  transition: width 0.3s ease;
`;

const Legend = styled.div`
  display: flex;
  align-items: center;
  gap: ${PRIMITIVES.spacings.b40}px;
  height: 35px;
  white-space: nowrap;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${SEMANTIC.spacings}px;
  font-size: ${SINGLE_LINE.small.regular.fontSize}px;
  color: ${(props) => props.theme.semanticTextDisabled};
`;

const LegendDot = styled.span<{ $color: string }>`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${(props) => props.$color};
`;

function StreamBuffer({ stats }: { stats: PipelineProcessStats }) {
  const cap = stats.capacity;
  const pct = (n: number) => (cap > 0 ? Math.min((n / cap) * 100, 100) : 0);
  const fullPct = cap > 0 ? (stats.pipeline_depth / cap) * 100 : 0;

  return (
    <Footer>
      <FooterInner>
        <BufferTitleRow>
          <span>
            Stream Buffer - {formatNumber(stats.pipeline_depth)} /{" "}
            {formatNumber(stats.capacity)}
          </span>
          {stats.source_blocked ? (
            <BlockedText>Source blocked</BlockedText>
          ) : (
            <span>{fullPct.toFixed(1)}% full</span>
          )}
        </BufferTitleRow>
        <BarTrack>
          <BarSegment $color={BUFFER_COLORS.queued} $pct={pct(stats.queued)} />
          <BarSegment
            $color={BUFFER_COLORS.inFlight}
            $pct={pct(stats.in_flight)}
          />
          <BarSegment $color={BUFFER_COLORS.done} $pct={pct(stats.done)} />
        </BarTrack>
        <Legend>
          <LegendItem>
            <LegendDot $color={BUFFER_COLORS.queued} />
            Queued {formatNumber(stats.queued)}
          </LegendItem>
          <LegendItem>
            <LegendDot $color={BUFFER_COLORS.inFlight} />
            In flight {formatNumber(stats.in_flight)}
          </LegendItem>
          <LegendItem>
            <LegendDot $color={BUFFER_COLORS.done} />
            Done {formatNumber(stats.done)}
          </LegendItem>
        </Legend>
      </FooterInner>
    </Footer>
  );
}

// ── Section ───────────────────────────────────────────────

interface MetricDisplay {
  events: string;
  eventsSub?: string;
  bytes: string;
  bytesSub?: string;
  throughput: string;
  lag: string;
  lagBadge: ReactNode;
  errors: string;
}

const EMPTY_DISPLAY: MetricDisplay = {
  events: "—",
  bytes: "—",
  throughput: "—",
  lag: "—",
  lagBadge: null,
  errors: "—",
};

// Derive every displayed string/badge from the metrics payload here so the
// component itself stays a flat render (keeps its complexity low). Events/bytes
// show the session figure with the all-time figure as a sub-label.
function toDisplay(metrics: JobMetrics | undefined): MetricDisplay {
  if (!metrics) return EMPTY_DISPLAY;
  return {
    events: formatNumber(
      metrics.session_events_processed ?? metrics.events_processed,
    ),
    eventsSub:
      metrics.session_events_processed != null
        ? `${formatNumber(metrics.events_processed)} all-time`
        : undefined,
    bytes: formatBytes(
      metrics.session_bytes_processed ?? metrics.bytes_processed,
    ),
    bytesSub:
      metrics.session_bytes_processed != null
        ? `${formatBytes(metrics.bytes_processed)} all-time`
        : undefined,
    throughput: `${formatRate(metrics.throughput_per_second)} events/sec`,
    lag: formatLag(metrics.current_lag_seconds),
    lagBadge: lagBadge(metrics.current_lag_seconds),
    errors: formatNumber(metrics.total_errors),
  };
}

function toneBadge(badge: WindowMetricCell["badge"]): ReactNode {
  if (!badge) return null;
  const byTone: Record<BadgeTone, typeof BadgeConfirmationSubtle> = {
    confirmation: BadgeConfirmationSubtle,
    warning: BadgeWarningSubtle,
    error: BadgeErrorSubtle,
  };
  const Badge = byTone[badge.tone];
  return <Badge>{badge.text}</Badge>;
}

const WindowNotes = styled.ul`
  margin: 0;
  padding: 0 ${SEMANTIC.spacingm}px ${SEMANTIC.spacingm}px;
  list-style: none;
  font-size: ${SINGLE_LINE.small.regular.fontSize}px;
  color: ${STATUS.error};
`;

/** A windowed pipeline's figures: what its windows settled, read from the
 *  shared store. Its records are not counted as they pass, so the event,
 *  byte, throughput and lag cells would read 0 and are not shown. */
function WindowMetrics({
  status,
  value,
}: {
  status: WindowStatusJson;
  value: (v: ReactNode) => ReactNode;
}) {
  const { cells, notes } = windowMetricsDisplay(status);
  return (
    <>
      <MetricScroll>
        <MetricGrid>
          {cells.map((cell) => (
            <MetricCell
              key={cell.label}
              label={cell.label}
              sub={cell.sub}
              value={value(cell.value)}
              badge={toneBadge(cell.badge)}
            />
          ))}
        </MetricGrid>
      </MetricScroll>
      {notes.length > 0 && (
        <WindowNotes>
          {notes.map((note) => (
            <li key={note}>{note}</li>
          ))}
        </WindowNotes>
      )}
    </>
  );
}

/** The record-by-record figures of an ordinary pipeline, and its stream
 *  buffer while it has one. */
function RecordMetrics({
  metrics,
  loading,
  value,
}: {
  metrics: JobMetrics | undefined;
  loading: boolean;
  value: (v: ReactNode) => ReactNode;
}) {
  const d = toDisplay(metrics);
  // The backend reports pipeline_process_stats only while the pipeline is
  // running and has a buffer to describe (null when stopped). In HA the value
  // is replica-independent, so the raw poll result is safe to render directly.
  const stats = metrics?.pipeline_process_stats ?? null;
  const showBuffer = !loading && stats != null && stats.capacity > 0;
  return (
    <>
      <MetricScroll>
        <MetricGrid>
          <MetricCell
            label="Events Processed"
            sub={loading ? undefined : d.eventsSub}
            value={value(d.events)}
          />
          <MetricCell
            label="Bytes Processed"
            sub={loading ? undefined : d.bytesSub}
            value={value(d.bytes)}
          />
          <MetricCell label="Throughput" value={value(d.throughput)} />
          <MetricCell
            label="Current Lag"
            value={value(d.lag)}
            badge={loading ? undefined : d.lagBadge}
          />
          <MetricCell label="Errors" value={value(d.errors)} />
        </MetricGrid>
      </MetricScroll>
      {showBuffer && <StreamBuffer stats={stats} />}
    </>
  );
}

export function MetricsSection({ jobName, isRunning }: MetricsSectionProps) {
  // While the first poll is in flight the values are unknown, not zero/em-dash
  // — show bars in the value pills. A stopped pipeline's disabled query is not
  // "loading" (react-query reports isLoading false), so it keeps the dashes.
  const { data: metrics, isLoading: loading } = useJobMetricsPolling(
    jobName,
    5000,
    isRunning,
  );
  const value = (v: ReactNode) =>
    loading ? <SkeletonBar $width="72px" $height="16px" /> : v;
  const windows = metrics?.windows;

  return (
    <SectionCard title="Metrics">
      {windows ? (
        <WindowMetrics status={windows} value={value} />
      ) : (
        <RecordMetrics metrics={metrics} loading={loading} value={value} />
      )}
    </SectionCard>
  );
}
